import { Line } from 'react-chartjs-2';
import Paper from '@trackman/web-shared-components/components/Paper/Paper';
import { ChartData, ChartOptions } from 'chart.js';
import { NoData } from 'pages/Admin/Analytics/components/NoData/NoData';
import { SourceBadge } from 'pages/Admin/Analytics/components/SourceBadge/SourceBadge';
import { currencyFormatter, formatMonth, indexByMonth, mergeMonthKeys, parseMonthKey } from 'pages/Admin/Analytics/utils/monthlySeries';
import { useGetCertRevenueBreakdown, useGetMonthlyRecurringRevenue } from 'queryHooks/Admin/analytics';

const chartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${currencyFormatter.format(Number(context.raw ?? 0))}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
    y: {
      grid: { color: 'rgba(0, 0, 0, 0.06)' },
      ticks: {
        font: { size: 11 },
        callback: (value) => currencyFormatter.format(Number(value)),
      },
    },
  },
};

export const MrrCertRevenueChart = () => {
  const { data: mrrData, isLoading: isLoadingMrr, isError: isMrrError } = useGetMonthlyRecurringRevenue();
  const { data: certRevenueData, isLoading: isLoadingCertRevenue, isError: isCertRevenueError } = useGetCertRevenueBreakdown();

  const isLoading = isLoadingMrr || isLoadingCertRevenue;
  const isError = isMrrError || isCertRevenueError;
  const hasData = Boolean(mrrData?.length || certRevenueData?.length);

  const keys = mergeMonthKeys(mrrData, certRevenueData);

  const mrrByMonth = indexByMonth(mrrData);
  const certRevenueByMonth = indexByMonth(certRevenueData);

  const chartData: ChartData<'line'> = {
    labels: keys.map((key) => {
      const { year, month } = parseMonthKey(key);
      return formatMonth(year, month);
    }),
    datasets: [
      {
        label: 'MRR (Subs)',
        data: keys.map((key) => mrrByMonth.get(key)?.mrr ?? null),
        borderColor: '#475569',
        backgroundColor: 'rgba(71, 85, 105, 0.15)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Cert Rev',
        data: keys.map((key) => {
          const entry = certRevenueByMonth.get(key);
          return entry ? entry.fullPriceIncome + entry.premiumIncome + entry.otherDiscountedIncome : null;
        }),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  return (
    <Paper className='analytics-card analytics-chart-card'>
      <div className='analytics-card__title-group'>
        <span className='card-title'>MRR & cert revenue</span>
        <SourceBadge source='RECURLY' />
      </div>
      <p className='analytics-card__subtitle'>Monthly recurring revenue against certification revenue breakdown.</p>

      {isLoading || isError || !hasData ? (
        <NoData loading={isLoading} error={isError} noData={!hasData} />
      ) : (
        <div className='analytics-chart-card__content'>
          <Line data={chartData} options={chartOptions} />
        </div>
      )}
    </Paper>
  );
};

export default MrrCertRevenueChart;
